import { ERentioFormField } from "@rentiohq/shared-frontend/dist/components/components/Form";
import { getValue } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  getFeedbackFieldForLocale,
  IExtraData,
} from "./schema.propertyApplicationNotEligible.types";
import {
  getLocalesInSelection,
  getMarkerFindingMessage,
  getMarkerProblems,
} from "./schema.propertyApplicationNotEligible.utils";

const LOCALE_LABEL_KEY: { [locale: string]: string } = {
  [ELocale.NL]: "property.application.not_eligible.message.label.nl",
  [ELocale.FR]: "property.application.not_eligible.message.label.fr",
  [ELocale.EN]: "property.application.not_eligible.message.label.en",
};

const getLabel = (locale: string) =>
  getLocalizedText(
    LOCALE_LABEL_KEY[locale] ||
      "property.application.not_eligible.message.label",
  );

export const getSchema =
  (
    initialValues: {
      [key: string]: any;
    },
    extraData: IExtraData,
  ) =>
  (defaultValues: { [key: string]: any }) => {
    const properties: { [field: string]: any } = {};
    const required: string[] = [];

    getLocalesInSelection(extraData.applications).forEach(({ locale }) => {
      const field = getFeedbackFieldForLocale(locale);
      const value = getValue(field, initialValues, defaultValues);

      properties[field] = { default: value };

      // Required only where the office has a message to start from, which is
      // every language it configured. A language it never set arrives empty,
      // and demanding a text there would stop the broker from moving those
      // candidates at all; left empty, they are sent nothing, as before.
      if (value) {
        required.push(field);
      }
    });

    return { required, properties };
  };

export const uiSchema = (extraData: IExtraData) => {
  const locales = getLocalesInSelection(extraData.applications);

  const fields: { [field: string]: any } = {};

  locales.forEach(({ locale, count }, index) => {
    fields[getFeedbackFieldForLocale(locale)] = {
      // The same editor the bulk-action mail uses, so a broker meets one
      // toolbar rather than two (DEV-3323). It stores HTML and reads it back,
      // which the plain textarea could not do.
      "ui:field": ERentioFormField.RichText,
      // The language is named even when there is only one, so a broker moving
      // a single French candidate can see that they are reading French rather
      // than wondering why the text is not in theirs.
      title: getLabel(locale),
      formatSummary: () => undefined,
      helpText: getLocalizedText(
        count === 1
          ? "property.application.not_eligible.message.recipients.one"
          : "property.application.not_eligible.message.recipients.many",
        { count: `${count}` },
      ),
      autoFocus: index === 0,
    };
  });

  return {
    "rentio:navigationTitle": getLocalizedText(
      "property.application.not_eligible.title",
    ),
    "ui:order": locales.map(({ locale }) => getFeedbackFieldForLocale(locale)),
    ...fields,
  };
};

/**
 * Refuses a text that has dropped the link its step depends on, or repeated
 * one that may appear only once.
 *
 * Both messages name the placeholder literally, because the broker has to put
 * that exact string back, and say what it is for in one line: a rule nobody
 * understands gets worked around rather than followed.
 */
export const getValidate =
  (extraData: IExtraData) => (formData: any, errors: any) => {
    getMarkerProblems(extraData, formData).forEach(finding => {
      errors[finding.field]?.addError(getMarkerFindingMessage(finding));
    });

    return errors;
  };
